import type { Message } from '@sbc/domain-models/agents';
import { Link } from '@tanstack/react-router';
import { ExternalLink, Play, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api/client';
import {
  useCsReview,
  useCsReviewProposals,
  useCsReviewSession,
  useSendCsReviewMessage,
} from '@/lib/api/cs-reviews';
import { Transcript } from '@/routes/sessions/transcript';

import { ProposalCard } from './proposal-card';

function lastSeq(messages: Message[], kind: Message['kind']): number {
  return messages.reduce(
    (latest, message) => (message.kind === kind ? Math.max(latest, message.seq) : latest),
    -1,
  );
}

function hasUnansweredTurn(messages: Message[]): boolean {
  const lastResponse = lastSeq(messages, 'response');
  return messages.some(
    (message) =>
      message.kind === 'request' &&
      message.seq > lastResponse &&
      message.parts.some((part) => part.partKind === 'user-prompt'),
  );
}

export function ReviewChatPage({ sessionId }: { sessionId: string }) {
  const reviewQuery = useCsReview(sessionId);
  const sessionQuery = useCsReviewSession(sessionId);
  const proposalsQuery = useCsReviewProposals({ sessionId });
  const send = useSendCsReviewMessage(sessionId);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const autoRan = useRef(false);

  const session = sessionQuery.data;
  const busy = send.isPending || session?.status === 'running';
  const unanswered = session ? hasUnansweredTurn(session.messages) : false;
  const neverRan = session ? lastSeq(session.messages, 'response') === -1 : false;

  const submit = (content: string | null) => {
    setError(null);
    send.mutate(content, {
      onSuccess: () => setDraft(''),
      onError: (caught) =>
        setError(caught instanceof ApiError ? caught.message : 'The reviewer did not answer.'),
    });
  };

  useEffect(() => {
    if (!autoRan.current && session?.status === 'idle' && neverRan && unanswered) {
      autoRan.current = true;
      submit(null);
    }
  });

  if (reviewQuery.isPending || sessionQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (reviewQuery.isError || sessionQuery.isError) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load the review: {(reviewQuery.error ?? sessionQuery.error)?.message}
      </p>
    );
  }

  const review = reviewQuery.data;
  const proposals = proposalsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Draft review · {review.intercomConversationId}
          </h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
            {review.conversationUrl && (
              <a
                href={review.conversationUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 hover:underline"
              >
                Open in Intercom <ExternalLink className="size-3" />
              </a>
            )}
            {review.articlePaths.map((path) => (
              <Badge key={path} variant="outline">
                {path}
              </Badge>
            ))}
          </div>
        </div>
        <Button variant="outline" asChild>
          <Link to="/review">New review</Link>
        </Button>
      </div>

      {review.draft && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              The draft ({review.draft.category} · {review.draft.confidence} confidence)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="whitespace-pre-wrap">{review.draft.body}</p>
            <p className="text-[var(--color-muted-foreground)]">{review.draft.reasoning}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Conversation with the reviewer</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {session && <Transcript messages={session.messages.filter((m) => m.seq > 0)} />}
          {session?.error && (
            <p className="text-sm text-[var(--color-destructive)]">
              Last run failed: {session.error}
            </p>
          )}
          <Textarea
            placeholder="Answer the reviewer, paste a source, or say what is wrong…"
            className="min-h-24"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={busy}
          />
          {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}
          <div className="flex items-center gap-2">
            <Button onClick={() => submit(draft.trim())} disabled={busy || !draft.trim()}>
              <Send className="size-4" />
              {busy ? 'Reviewer is thinking…' : 'Send'}
            </Button>
            {unanswered && !busy && (
              <Button variant="outline" onClick={() => submit(null)}>
                <Play className="size-4" />
                Ask the reviewer again
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {proposals.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold tracking-tight">Proposals</h2>
          {proposals.map((proposal) => (
            <ProposalCard key={proposal.id} proposal={proposal} />
          ))}
        </div>
      )}
    </div>
  );
}
